import React, { useMemo, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../utils/api';
import { getVerticalBranding, getVerticalAppStyles, getVerticalNavLabels, normalizeVertical, type VerticalType } from '../config/vertical';
import { OfflineIndicator } from './OfflineComponents';
import HelpSidebar from './HelpSidebar';
import NotificationCenter from './NotificationCenter';
import ReadOnlyBanner from './ReadOnlyBanner';
import ReachOutModal from './ReachOutModal';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  name: string;
  href: string;
  ownerOnly?: boolean;
}

interface NavigationGroup {
  id: string;
  name: string;
  items: NavigationItem[];
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reachOutOpen, setReachOutOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['vineyard']));
  const [grapiaOperatorVisible, setGrapiaOperatorVisible] = useState(false);

  const vertical: VerticalType = useMemo(
    () => normalizeVertical(user?.organization_type),
    [user?.organization_type]
  );
  const { brandName, logoSrc } = useMemo(() => getVerticalBranding(vertical), [vertical]);
  const appStyles = useMemo(() => getVerticalAppStyles(vertical), [vertical]);
  const navLabels = useMemo(() => getVerticalNavLabels(vertical), [vertical]);

  React.useEffect(() => {
    if (!user?.vineyard_id || user?.role === 'worker') {
      setGrapiaOperatorVisible(false);
      return;
    }

    let cancelled = false;
    api.get('/api/ai/operator/summary', { params: { vineyard_id: user.vineyard_id } })
      .then((response) => {
        if (!cancelled) {
          setGrapiaOperatorVisible(!!response.data?.visible);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGrapiaOperatorVisible(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.role, user?.vineyard_id]);

  // Check if user is owner (vineyard isolation: single role)
  const isOwner = user?.role === 'owner';
  const isReadOnlyUiLocked = !!user?.is_read_only && !location.pathname.startsWith('/subscription');

  const navigationGroups: NavigationGroup[] = useMemo(
    () => [
      {
        id: 'vineyard',
        name: navLabels.managementGroupName,
        items: [
          { name: 'Dashboard', href: '/dashboard' },
          { name: 'Crew Control', href: '/manager-dashboard' },
          { name: 'Manager Tasks', href: '/manager-tasks' },
          { name: `${navLabels.unitLabel} Dashboard`, href: '/block-dashboard' },
          { name: 'Machinery', href: '/machinery' },
          { name: 'Monitoring', href: '/ai/monitoring' },
          { name: 'AI Insights', href: '/ai/insights' },
          ...(grapiaOperatorVisible ? [{ name: 'Grapia Operator', href: '/ai/operator' }] : [])
        ]
      },
      {
        id: 'production',
        name: 'Production Operations',
        items: [
          { name: 'Inventory', href: '/inventory' },
          { name: 'Inputs', href: '/inputs' },
          { name: 'Scouting', href: '/scouting' },
          { name: 'Sensor Data', href: '/sensors' },
          { name: 'Disease Pressure', href: '/disease-pressure' },
          { name: 'Quality Gates', href: '/completed-quality-gates' },
          { name: 'Harvests', href: '/harvests' },
          { name: 'Maintenance', href: '/maintenance' },
          { name: 'Dictionary', href: '/dictionary' },
          { name: 'Calculations', href: '/calculations' }
        ]
      },
      {
        id: 'quality',
        name: 'Quality',
        items: [
          // Analytics temporarily hidden from navigation (route still works via direct URL)
          // { name: 'Analytics', href: '/analytics' },
          { name: 'Wine Tasting', href: '/wine-tasting' },
          { name: 'Contracts', href: '/contracts' },
          { name: 'Permits', href: '/permits' }
        ]
      },
      {
        id: 'setup',
        name: 'Setup & Reference',
        items: [
          { name: navLabels.entityName, href: '/vineyard' },
          { name: navLabels.unitLabelPlural, href: '/blocks' },
          { name: 'Equipment', href: '/equipment' },
          { name: 'People Management', href: '/user-management' },
          { name: 'Reference Data', href: '/reference-data' },
          { name: 'Sensor Configuration', href: '/sensors/config' },
          { name: 'Time Export', href: '/time-export' }
        ]
      },
      {
        id: 'billing',
        name: 'Billing',
        items: [
          { name: 'Subscription', href: '/subscription', ownerOnly: true }
        ]
      }
    ],
    [grapiaOperatorVisible, navLabels]
  );

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  // Track menu click for activity log (fire-and-forget; do not block navigation)
  const trackMenuClick = useCallback((item: NavigationItem) => {
    // Avoid newer `dateStyle/timeStyle/timeZoneName` options that can throw
    // in some JS runtime environments.
    const clientLocalTime = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    api.post('/api/activity/menu-click', {
      menu_item_name: item.name,
      menu_item_href: item.href,
      client_local_time: clientLocalTime,
    }).catch(() => {});
  }, []);

  // Determine user role for help content
  const getUserRole = () => {
    if (!user) return 'worker';
    if (user.is_superuser) return 'superuser';
    if (user.role && ['owner', 'admin', 'manager'].includes(user.role)) {
      return 'management';
    }
    return 'worker';
  };

  React.useEffect(() => {
    const root = document.documentElement;
    if (!root) {
      return;
    }

    const lockClass = 'read-only-ui-lock';
    const markerAttr = 'data-read-only-disabled';
    const priorAttr = 'data-read-only-prior-disabled';

    const lockSubmitControls = () => {
      const controls = document.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
        'button[type="submit"], input[type="submit"]'
      );
      controls.forEach((control) => {
        if (control.hasAttribute(markerAttr)) {
          return;
        }
        control.setAttribute(priorAttr, control.disabled ? '1' : '0');
        control.disabled = true;
        control.setAttribute(markerAttr, '1');
      });
    };

    const unlockSubmitControls = () => {
      const controls = document.querySelectorAll<HTMLElement>(`[${markerAttr}="1"]`);
      controls.forEach((control) => {
        const prior = control.getAttribute(priorAttr);
        (control as HTMLButtonElement | HTMLInputElement).disabled = prior === '1';
        control.removeAttribute(markerAttr);
        control.removeAttribute(priorAttr);
      });
    };

    let observer: MutationObserver | null = null;

    if (isReadOnlyUiLocked) {
      root.classList.add(lockClass);
      lockSubmitControls();
      observer = new MutationObserver(() => lockSubmitControls());
      observer.observe(document.body, { childList: true, subtree: true });
    } else {
      root.classList.remove(lockClass);
      unlockSubmitControls();
    }

    return () => {
      observer?.disconnect();
      root.classList.remove(lockClass);
      unlockSubmitControls();
    };
  }, [isReadOnlyUiLocked]);

  return (
    <div className={`min-h-screen ${appStyles.mainBg}`}>
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-metal-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 h-screen min-h-0 flex-col bg-vineyard-white">
          <div className="flex h-16 shrink-0 items-center justify-between px-4">
            <div className="flex items-center">
              <img
                src={logoSrc}
                alt={`${brandName} Logo`}
                className="h-12 w-auto object-contain"
              />
              <span className="font-brand ml-2 text-lg font-semibold text-wood-900">{brandName}</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-metal-500 hover:text-metal-700"
            >
              Close
            </button>
          </div>
          <nav className="min-h-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-2 py-4 mobile-nav-scroll">
            {navigationGroups.map((group, groupIndex) => (
              <div key={group.id} className="space-y-1">
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-wood-800 hover:bg-vineyard-light rounded transition-colors duration-200"
                >
                  <span className="text-xs font-semibold uppercase tracking-wider text-metal-600">
                    {group.name}
                  </span>
                  {expandedGroups.has(group.id) ? (
                    <span className="text-metal-500">▼</span>
                  ) : (
                    <span className="text-metal-500">▶</span>
                  )}
                </button>
                {expandedGroups.has(group.id) && (
                  <div className="space-y-0.5 ml-3 border-l-2 border-metal-200 pl-3">
                    {group.items.map((item) => {
                      if (item.ownerOnly && !isOwner) return null;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group flex items-center px-3 py-1.5 text-sm font-medium rounded transition-colors duration-200 ${
                            isActive(item.href)
                              ? `${appStyles.navActiveBg} ${appStyles.navActiveText} ${appStyles.navActiveBorder} font-semibold shadow-sm`
                              : 'text-metal-700 hover:bg-vineyard-light hover:text-wood-900'
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(item.href);
                            trackMenuClick(item);
                            setSidebarOpen(false);
                          }}
                        >
                          <span className="flex-1">{item.name}</span>
                          {isActive(item.href) && (
                            <span className={`${appStyles.navActiveDot} text-xs font-bold ml-2`}>●</span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
                {groupIndex < navigationGroups.length - 1 && (
                  <div className="border-t border-metal-200 my-2"></div>
                )}
              </div>
            ))}
          </nav>
          {user && (
            <footer className={`flex-shrink-0 border-t ${appStyles.footerBorder} px-4 py-3 flex items-center justify-between gap-2 ${appStyles.footerBg}`}>
              <span className="text-sm font-bold text-wood-600">{brandName}</span>
              <button
                type="button"
                onClick={() => { setReachOutOpen(true); setSidebarOpen(false); }}
                className={`text-sm font-bold ${appStyles.linkAccent} touch-manipulation`}
              >
                Reach Out
              </button>
            </footer>
          )}
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:h-screen lg:flex-col">
        <div className={`flex flex-col flex-1 min-h-0 w-full bg-vineyard-white border-r-2 ${appStyles.sidebarBorder}`}>
          <div className="flex h-16 items-center px-4">
            <div className="flex items-center">
              <img
                src={logoSrc}
                alt={`${brandName} Logo`}
                className="h-12 w-auto object-contain"
              />
              <span className="font-brand ml-2 text-lg font-semibold text-wood-900">{brandName}</span>
            </div>
          </div>
          <nav className="flex-1 space-y-2 px-2 py-4 overflow-y-auto min-h-0">
            {navigationGroups.map((group, groupIndex) => (
              <div key={group.id} className="space-y-1">
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-wood-800 hover:bg-vineyard-light rounded transition-colors duration-200"
                >
                  <span className="text-xs font-semibold uppercase tracking-wider text-metal-600">
                    {group.name}
                  </span>
                  {expandedGroups.has(group.id) ? (
                    <span className="text-metal-500">▼</span>
                  ) : (
                    <span className="text-metal-500">▶</span>
                  )}
                </button>
                {expandedGroups.has(group.id) && (
                  <div className="space-y-0.5 ml-3 border-l-2 border-metal-200 pl-3">
                    {group.items.map((item) => {
                      if (item.ownerOnly && !isOwner) return null;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group flex items-center px-3 py-1.5 text-sm font-medium rounded transition-colors duration-200 ${
                            isActive(item.href)
                              ? `${appStyles.navActiveBg} ${appStyles.navActiveText} ${appStyles.navActiveBorder} font-semibold shadow-sm`
                              : 'text-metal-700 hover:bg-vineyard-light hover:text-wood-900'
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(item.href);
                            trackMenuClick(item);
                          }}
                        >
                          <span className="flex-1">{item.name}</span>
                          {isActive(item.href) && (
                            <span className={`${appStyles.navActiveDot} text-xs font-bold ml-2`}>●</span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
                {groupIndex < navigationGroups.length - 1 && (
                  <div className="border-t border-metal-200 my-2"></div>
                )}
              </div>
            ))}
          </nav>
          {user && (
            <footer className={`flex-shrink-0 border-t ${appStyles.footerBorder} px-4 py-3 flex items-center justify-between gap-2 ${appStyles.footerBg}`}>
              <span className="text-sm font-bold text-wood-600">{brandName}</span>
              <button
                type="button"
                onClick={() => setReachOutOpen(true)}
                className={`text-sm font-bold ${appStyles.linkAccent} touch-manipulation`}
              >
                Reach Out
              </button>
            </footer>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className={`sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 ${appStyles.headerBarClass} px-4 shadow-lg sm:gap-x-6 sm:px-6 lg:px-8`}>
          <button
            type="button"
            className="-m-2.5 p-2.5 text-white hover:text-wood-50 lg:hidden touch-manipulation"
            onClick={() => setSidebarOpen(true)}
          >
            Menu
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-2 sm:gap-x-4 lg:gap-x-6">
              <OfflineIndicator />
              <NotificationCenter />
              <button
                onClick={() => setHelpOpen(true)}
                className="flex items-center gap-x-1 sm:gap-x-2 text-sm font-medium text-white hover:text-wood-50 touch-manipulation p-1 sm:p-0"
                title="Help & About"
              >
                <span>Help</span>
              </button>
              <div className="flex items-center gap-x-2">
                <span className="text-sm font-medium text-white hidden sm:block">
                  {user?.full_name || user?.username}
                </span>
                <span className="text-sm font-medium text-white sm:hidden">
                  {user?.full_name?.split(' ')[0] || user?.username}
                </span>
              </div>
              <button
                onClick={() => logout()}
                className="flex items-center gap-x-1 sm:gap-x-2 text-sm font-medium text-white hover:text-wood-50 touch-manipulation p-1 sm:p-0"
              >
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Read-Only Banner */}
        <ReadOnlyBanner />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* Help Sidebar */}
      <HelpSidebar
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        userRole={getUserRole() as 'worker' | 'management' | 'superuser'}
        onOpenReachOut={() => setReachOutOpen(true)}
      />

      {/* Reach Out modal */}
      <ReachOutModal isOpen={reachOutOpen} onClose={() => setReachOutOpen(false)} />
    </div>
  );
};

export default Layout; 
